import React, { useState, useEffect, useMemo, useRef } from "react"
import { withFormik, Form, Field } from "formik"
import { string, object } from "yup"
import Grid from "@material-ui/core/Grid"
import styles from "./SignUpApplyForm.module.scss"
import Dropdown from "../../components/Dropdown/Dropdown"
import Input from "../../components/Input/Input"
import {
  whichBusinessTypeOptions,
  bestDescribesOptions,
} from "./SignUpApplyValuesHelper"

const SignUpApplyStep1Form = ({
  values,
  errors,
  handleSubmit,
  handleChange,
  handleBlur,
  touched,
  isSubmitting,
  userType,
  onSubmit,
  onValidityChange,
  initialValues,
  isValid,
  dirty,
  setFieldTouched,
  setFieldValue,
}) => {
  // Filter bestDescribesOptions based on selected business type
  const filteredBestDescribesOptions = useMemo(() => {
    // We don't any options as for latest requirements
    return bestDescribesOptions
  }, [values.whichBusinessType])

  const isProfessionalCategory = values.bestDescribes === "professional"
  const filteredWhichBusinessTypeOptions = useMemo(() => {
    if (isProfessionalCategory) {
      return whichBusinessTypeOptions.filter((o) => o.id === "company_seeker")
    }
    return whichBusinessTypeOptions
  }, [isProfessionalCategory])

  // Business rule: "Professional" can only be a seeker (not a provider).
  // Auto-set to seeker and lock the field when category is Professional.
  const didWarnRef = useRef(false)
  const prevBestDescribesRef = useRef(values.bestDescribes)
  useEffect(() => {
    const prevBestDescribes = prevBestDescribesRef.current
    prevBestDescribesRef.current = values.bestDescribes

    if (!isProfessionalCategory) {
      didWarnRef.current = false
      return
    }

    const switchedToProfessional =
      prevBestDescribes !== "professional" &&
      values.bestDescribes === "professional"
    const wasProviderNowProfessional =
      switchedToProfessional && values.whichBusinessType === "company_provider"

    if (wasProviderNowProfessional && !didWarnRef.current) {
      didWarnRef.current = true
      // Small popup per requirement (only when switching from Provider -> Professional)
      window.alert("Professional cannot be a provider. Switching to seeker.")
    }

    if (values.whichBusinessType !== "company_seeker") {
      setFieldValue("whichBusinessType", "company_seeker")
      setFieldTouched("whichBusinessType", true, false)
    }
  }, [
    isProfessionalCategory,
    values.whichBusinessType,
    setFieldTouched,
    setFieldValue,
  ])
  // Mark fields as touched when initialValues are provided
  useEffect(() => {
    if (initialValues) {
      const fields = [
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "whichBusinessType",
        "bestDescribes",
      ]
      fields.forEach((field) => {
        if (initialValues[field]) {
          setFieldTouched(field, true, false)
        }
      })
    }
  }, [initialValues, setFieldTouched])

  useEffect(() => {
    const requiredFields = [
      "firstName",
      "lastName",
      "email",
      "phoneNumber",
      "whichBusinessType",
      "bestDescribes",
    ]

    const hasErrors = requiredFields.some(
      (field) => errors[field] && touched[field],
    )
    const allFieldsTouched = requiredFields.every((field) => touched[field])
    const allFieldsFilled = requiredFields.every((field) => values[field])

    onValidityChange(!hasErrors && allFieldsTouched && allFieldsFilled && dirty)
  }, [values, errors, touched, dirty, onValidityChange])

  return (
    <form className={styles.form} onSubmit={handleSubmit} id="formikForm">
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Input
            name="firstName"
            className={styles.field}
            placeholder="Type your First name"
            label="First Name"
            value={values.firstName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.firstName && touched.firstName}
            errorMessage={
              errors.firstName && touched.firstName ? errors.firstName : ""
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Input
            name="lastName"
            className={styles.field}
            placeholder="Type your Last name"
            label="Last Name"
            value={values.lastName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.lastName && touched.lastName}
            errorMessage={
              errors.lastName && touched.lastName ? errors.lastName : ""
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Input
            name="email"
            className={styles.field}
            placeholder="Type your email"
            label="Email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.email && touched.email}
            errorMessage={errors.email && touched.email ? errors.email : ""}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Input
            name="phoneNumber"
            className={styles.field}
            placeholder="Type your phone number"
            label="Phone number"
            value={values.phoneNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.phoneNumber && touched.phoneNumber}
            errorMessage={
              errors.phoneNumber && touched.phoneNumber
                ? errors.phoneNumber
                : ""
            }
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <Dropdown
            name="whichBusinessType"
            className={styles.dropdownField}
            label="Please select whether you interested in providing or seeking opportunities."
            options={filteredWhichBusinessTypeOptions}
            disabled={isProfessionalCategory}
            value={
              values.whichBusinessType
                ? {
                    id: values.whichBusinessType,
                    value:
                      whichBusinessTypeOptions.find(
                        (item) => item.id === values.whichBusinessType,
                      )?.value || "",
                  }
                : {}
            }
            onChange={(name, option) =>
              handleChange({ target: { name, value: option.id } })
            }
            onBlur={handleBlur}
            error={errors.whichBusinessType && touched.whichBusinessType}
            errorMessage={
              errors.whichBusinessType && touched.whichBusinessType
                ? errors.whichBusinessType
                : ""
            }
            id="whichBusinessType"
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <Dropdown
            name="bestDescribes"
            className={styles.dropdownField}
            label="Please select which of the following best describes you today."
            options={filteredBestDescribesOptions}
            value={
              values.bestDescribes
                ? {
                    id: values.bestDescribes,
                    value:
                      filteredBestDescribesOptions.find(
                        (item) => item.id === values.bestDescribes,
                      )?.value || "",
                  }
                : {}
            }
            onChange={(name, option) =>
              handleChange({ target: { name, value: option.id } })
            }
            onBlur={handleBlur}
            error={errors.bestDescribes && touched.bestDescribes}
            errorMessage={
              errors.bestDescribes && touched.bestDescribes
                ? errors.bestDescribes
                : ""
            }
            id="bestDescribes"
          />
        </Grid>
      </Grid>
      <button
        type="submit"
        className={styles.submitBtn}
        style={{ display: "none" }}
      >
        Submit
      </button>
    </form>
  )
}

const FormikApp = withFormik({
  mapPropsToValues({
    firstName,
    lastName,
    email,
    phoneNumber,
    whichBusinessType,
    bestDescribes,
    initialValues,
  }) {
    return {
      firstName: initialValues?.firstName || firstName || "",
      lastName: initialValues?.lastName || lastName || "",
      email: initialValues?.email || email || "",
      phoneNumber: initialValues?.phoneNumber || phoneNumber || "",
      whichBusinessType:
        initialValues?.whichBusinessType || whichBusinessType || "",
      bestDescribes: initialValues?.bestDescribes || bestDescribes || "",
    }
  },
  validationSchema: object().shape({
    firstName: string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .matches(
        /^[a-zA-Z\s-]+$/,
        "First name can only contain letters, spaces, and hyphens",
      ),
    lastName: string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .matches(
        /^[a-zA-Z\s-]+$/,
        "Last name can only contain letters, spaces, and hyphens",
      ),
    email: string()
      .required("Email is required")
      .email("Please enter a valid email address")
      .max(255, "Email must not exceed 255 characters"),
    phoneNumber: string()
      .required("Phone number is required")
      .matches(/^[0-9+\s()-]{10,}$/, "Please enter a valid phone number"),
    whichBusinessType: string().when("bestDescribes", {
      is: "professional",
      then: string()
        .required("Please select your business type")
        .oneOf(["company_seeker"], "Professional can only be a seeker"),
      otherwise: string()
        .required("Please select your business type")
        .oneOf(
          ["company_seeker", "company_provider"],
          "Please select a valid business type",
        ),
    }),
    bestDescribes: string()
      .required("Please select what best describes you")
      .oneOf(
        [
          "emerging",
          "corporation",
          "financial",
          "nonprofit",
          "education",
          "government",
          "professional",
          "other",
        ],
        "Please select a valid option",
      ),
  }),

  handleSubmit(values, { props }) {
    if (props.onSubmit) {
      props.onSubmit(values)
    }
  },
})(SignUpApplyStep1Form)

export default FormikApp
